import { type ChangeEvent, Component, type ReactNode } from 'react';
import Gravatar from 'react-gravatar';
import { Button, FormGroup, InputGroup, Intent, NumericInput, Tag } from '@blueprintjs/core';
import { Focusable, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { format, formatDistanceToNow, subMonths } from 'date-fns';
import { type Team } from 'launchdarkly-api-typescript';
import { debounce } from 'lodash';

import { type Scopes } from '../../auth';
import { type Settings } from '../../settings';
import { BooleanTag, CircularIconButton, CopyToClipboard, Loading, Module, ModuleContent, Table } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { observableToPromise } from '../../shared/observableToPromise';
import { linkToSessionReplay } from '../../shared/third-party';
import { getTeamWithRolesAndAttributes } from '../api';
import {
  type Account,
  type CustomRole,
  getMemberDisplayName,
  hasName,
  isOwner,
  type Member,
  type MemberSessions,
  type MembersPaginationData,
  type TeamsData,
} from '../model';
import { pluralize } from '../utils';

import { getAuthMethodInfo } from './authMethod';
import { AuthMethodTag } from './AuthMethodTag';
import { CustomRoleDetails } from './CustomRoleDetails';
import { consolidatePermissions, ConsolidationModal, shouldShowConsolidationButton } from './CustomRoles';
import { MemberActionsMenu } from './MemberActionsMenu';
import { MFAStatusTag } from './MFAStatusTag';

import './members.scss';

// Minimum term length that participates in filtering. Mirrors FILTER_MIN_LENGTH
// in the container so highlighting matches what the filter actually keys on.
const FILTER_HIGHLIGHT_MIN_LENGTH = 2;

// Parse a comma-separated filter value into the same normalized terms the
// container uses (whitespace stripped, lowercased, short terms dropped).
const parseHighlightTerms = (raw: string): string[] =>
  (raw || '')
    .split(',')
    .map((term) => term.replace(/\s+/g, '').toLowerCase())
    .filter((term) => term.length >= FILTER_HIGHLIGHT_MIN_LENGTH);

// Whether a role/team key substring-matches any active filter term. Used purely
// for visual highlighting; the authoritative filtering lives in the container.
export const keyMatchesFilter = (key: string | undefined, rawFilter: string): boolean => {
  const terms = parseHighlightTerms(rawFilter);
  if (terms.length === 0 || !key) {
    return false;
  }
  const normalized = key.toLowerCase();
  return terms.some((term) => normalized.includes(term));
};

// Intent for a member's built-in base role tag. Highlights (WARNING) when the
// base role matches the active role filter, otherwise keeps the owner/none look.
const baseRoleTagIntent = (member: Member, roleFilter: string): Intent => {
  if (keyMatchesFilter(member.role, roleFilter)) {
    return Intent.WARNING;
  }
  if (isOwner(member)) {
    return Intent.PRIMARY;
  }
  return Intent.NONE;
};

type MembersPanelProps = Readonly<{
  account: Account;
  settings: Settings;
  scopes: Scopes;
  memberSessions: MemberSessions[];
  members: Member[];
  customRoles: { [accountId: string]: CustomRole };
  onSetMfaDisabled: (account: Account, member: Member) => void;
  handlePaginateMembers: (url: string) => void;
  handleSearchMembers: (query: string) => void; // Add this prop
  handleRoleFilterChange: (filter: string) => void;
  handleTeamFilterChange: (filter: string) => void;
  handleResendInvitation: (memberId: string) => void;
  onMembersRefresh: () => void;
  customRolesForMemberInTeams: (member: Member, customRoles: { [accountId: string]: CustomRole }) => CustomRole[];
  customRolesWithTeamsForMember: (
    member: Member,
    customRoles: { [accountId: string]: CustomRole },
  ) => Array<{ role: CustomRole; team: TeamsData }>;
  getMemberSession: (memberId: string) => MemberSessions[];
  getMemberLastActive: (memberId: string, dateFormat: string) => ReactNode;
  paginationData: MembersPaginationData;
  isFetching: boolean;
  roleFilter: string;
  teamFilter: string;
  fetchProgress: { current: number; total: number } | null;
  isFilterActive: boolean;
  filterHitCap: boolean;
  filterResultCap: number;
  marketplaceProvider?: string;
}>;

type StateProps = {
  monthsAgo: number;
  searchQuery: string;
  roleFilterInput: string;
  teamFilterInput: string;
  teamData: { [teamKey: string]: { roleAttributes: { [key: string]: string[] } } };
};

export class MembersPanel extends Component<MembersPanelProps, StateProps> {
  public state: StateProps = {
    monthsAgo: 1,
    searchQuery: '',
    roleFilterInput: '',
    teamFilterInput: '',
    teamData: {},
  };

  private debouncedSearch = debounce((query: string) => {
    this.props.handleSearchMembers(query);
  }, 400);

  private debouncedRoleFilter = debounce((filter: string) => {
    this.props.handleRoleFilterChange(filter);
  }, 800);

  private debouncedTeamFilter = debounce((filter: string) => {
    this.props.handleTeamFilterChange(filter);
  }, 800);

  public componentDidUpdate(prevProps: MembersPanelProps) {
    if (prevProps.account._id !== this.props.account._id && this.props.settings.enableRoleAndTeamFilter) {
      this.setState({
        searchQuery: '',
        roleFilterInput: '',
        teamFilterInput: '',
      });
    }
  }

  public componentWillUnmount() {
    this.debouncedSearch.cancel();
    this.debouncedRoleFilter.cancel();
    this.debouncedTeamFilter.cancel();
  }

  public render() {
    const {
      account,
      customRoles,
      members,
      settings,
      scopes,
      handlePaginateMembers,
      paginationData,
      getMemberLastActive,
      customRolesWithTeamsForMember,
      isFetching,
      isFilterActive,
      filterHitCap,
      filterResultCap,
    } = this.props;

    const showActionsColumn = settings.enableMemberMfaReset && scopes.memberMfaReset;
    // When the role filter is active we dim non-matching (green) role badges so
    // the amber matches stand out.
    const roleFilterActive = parseHighlightTerms(this.props.roleFilter).length > 0;
    return (
      <Flex>
        <Box width={1} p={1}>
          <Flex justifyContent="space-between" className="MembersPanel-searchSection">
            {/* Search and filter inputs */}
            {settings.enableRoleAndTeamFilter ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1, maxWidth: '800px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <TooltipTrigger>
                    <Focusable>
                      <div style={{ flex: 1 }}>
                        <InputGroup
                          leftIcon="search"
                          placeholder="Search members..."
                          value={this.state.searchQuery}
                          onChange={this.handleSearchChange}
                          rightElement={this.renderClearButton(this.state.searchQuery, this.handleClearSearch)}
                          fill
                        />
                      </div>
                    </Focusable>
                    <Tooltip placement="top">Search members by name or email address.</Tooltip>
                  </TooltipTrigger>
                  <TooltipTrigger>
                    <Focusable>
                      <div style={{ flex: 1 }}>
                        <InputGroup
                          leftIcon="filter"
                          placeholder="Filter by role (comma-separated)..."
                          value={this.state.roleFilterInput}
                          onChange={this.handleRoleFilterChange}
                          rightElement={this.renderClearButton(this.state.roleFilterInput, this.handleClearRoleFilter)}
                          fill
                        />
                      </div>
                    </Focusable>
                    <Tooltip placement="top">
                      Filter by role key, including team-inherited roles (base role when a member has none).
                      Comma-separate multiple roles. Matching roles are highlighted. Shows up to 20 results.
                    </Tooltip>
                  </TooltipTrigger>
                  <TooltipTrigger>
                    <Focusable>
                      <div style={{ flex: 1 }}>
                        <InputGroup
                          leftIcon="filter"
                          placeholder="Filter by team key (comma-separated)..."
                          value={this.state.teamFilterInput}
                          onChange={this.handleTeamFilterChange}
                          rightElement={this.renderClearButton(this.state.teamFilterInput, this.handleClearTeamFilter)}
                          fill
                        />
                      </div>
                    </Focusable>
                    <Tooltip placement="top">
                      Filter by team key. Comma-separate multiple teams. Matching team keys are highlighted. Shows up to
                      20 results.
                    </Tooltip>
                  </TooltipTrigger>
                </div>
                {!isFetching &&
                  members.length > 0 &&
                  (this.props.roleFilter.trim().length >= 2 || this.props.teamFilter.trim().length >= 2) && (
                    <div style={{ fontSize: '12px', color: '#5c7080' }}>
                      {(() => {
                        const count = members.length;
                        const shownCount = filterHitCap ? filterResultCap : count;
                        const memberText = shownCount === 1 ? 'member' : 'members';
                        const hasRole = this.props.roleFilter.trim().length >= 2;
                        const hasTeam = this.props.teamFilter.trim().length >= 2;

                        const prefix = filterHitCap
                          ? `Showing the first ${filterResultCap} ${memberText}`
                          : `Showing ${count} ${memberText}`;
                        const suffix = filterHitCap ? ' — refine your filter to narrow results' : '';

                        let clause = '';
                        if (hasRole && hasTeam) {
                          clause = ` matching the "${this.props.roleFilter}" role and "${this.props.teamFilter}" team filter`;
                        } else if (hasRole) {
                          clause = ` matching the "${this.props.roleFilter}" role filter`;
                        } else if (hasTeam) {
                          clause = ` matching the "${this.props.teamFilter}" team filter`;
                        } else {
                          return '';
                        }

                        return `${prefix}${clause}${suffix}`;
                      })()}
                    </div>
                  )}
              </div>
            ) : (
              <InputGroup
                leftIcon="search"
                placeholder="Search members..."
                value={this.state.searchQuery}
                onChange={this.handleSearchChange}
              />
            )}
            {settings.enableMemberSessions && (
              <div className="AccountDetails-memberActivityCount">
                <div>
                  <strong>{this.getActiveMembersCount()}</strong> active members the past
                </div>
                <FormGroup className="MembersPanel-monthsAgoForm">
                  <NumericInput
                    className="MembersPanel-monthsAgoInput"
                    min={1}
                    stepSize={1}
                    id="events-published"
                    name="months-ago"
                    value={this.state.monthsAgo}
                    onValueChange={(value) => this.setState({ monthsAgo: value && value >= 1 ? value : 1 })}
                  />
                </FormGroup>
                {pluralize('month', this.state.monthsAgo)}
              </div>
            )}
            {!isFilterActive && settings.enablePaginationOfMembers && (
              <Flex>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <IconButton
                    icon="chevrons-left"
                    variant="minimal"
                    onPress={() => handlePaginateMembers(paginationData.links.first.href)}
                    isDisabled={
                      (paginationData &&
                        paginationData.links &&
                        paginationData.links.first &&
                        paginationData.links.first.href) === undefined
                    }
                    aria-label="Go to first page"
                  />
                  <IconButton
                    icon="chevron-left"
                    variant="minimal"
                    onPress={() => handlePaginateMembers(paginationData.links.prev.href)}
                    isDisabled={
                      (paginationData &&
                        paginationData.links &&
                        paginationData.links.prev &&
                        paginationData.links.prev.href) === undefined
                    }
                    aria-label="Go to previous page"
                  />
                  <IconButton
                    icon="chevron-right"
                    variant="minimal"
                    onPress={() => handlePaginateMembers(paginationData.links.next.href)}
                    isDisabled={
                      (paginationData &&
                        paginationData.links &&
                        paginationData.links.next &&
                        paginationData.links.next.href) === undefined
                    }
                    aria-label="Go to next page"
                  />
                  <IconButton
                    icon="chevrons-right"
                    variant="minimal"
                    onPress={() => handlePaginateMembers(paginationData.links.last.href)}
                    isDisabled={
                      (paginationData &&
                        paginationData.links &&
                        paginationData.links.last &&
                        paginationData.links.last.href) === undefined
                    }
                    aria-label="Go to last page"
                  />
                </div>
              </Flex>
            )}
          </Flex>
          <Module>
            <ModuleContent snug>
              <Table>
                <colgroup>
                  <col className="TableColumn-20" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-10" />
                  <col className="TableColumn-20" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    {settings.enableTeamsVisibility !== 'teams_off' && <th>Team</th>}
                    <th>Consolidated role</th>
                    <th>MFA</th>
                    {settings.enableAuthMethodColumn && <th>AuthN method</th>}
                    <th>Pending invite</th>
                    {settings.enableMemberSessions && <th>Last active</th>}
                    <th>ID</th>
                    <th>Last seen</th>
                    <th>Sessions</th>
                    {showActionsColumn && <th aria-label="Actions" />}
                  </tr>
                </thead>
                <>
                  {isFetching ? (
                    <tbody>
                      <tr>
                        <td
                          colSpan={
                            8 +
                            (settings.enableTeamsVisibility !== 'teams_off' ? 1 : 0) +
                            (settings.enableMemberSessions ? 1 : 0) +
                            (settings.enableAuthMethodColumn ? 1 : 0) +
                            (showActionsColumn ? 1 : 0)
                          }
                        >
                          <Flex justifyContent="center">
                            <Box>
                              {this.props.fetchProgress ? (
                                <Loading
                                  title={`Filtering through all members (${this.props.fetchProgress.current} / ${this.props.fetchProgress.total})`}
                                />
                              ) : (
                                <Loading title="Loading members" />
                              )}
                            </Box>
                          </Flex>
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <>
                      {members && members.length > 0 ? (
                        <tbody>
                          {members.map((m) => (
                            <tr key={m._id}>
                              <td>
                                <Flex alignItems="center">
                                  <Box pr={4} alignSelf="center">
                                    <Gravatar email={m.email} size={24} />
                                  </Box>
                                  <Box>
                                    <Flex flexDirection="column">
                                      <Box alignSelf="flex-start">{getMemberDisplayName(m)}</Box>
                                      {hasName(m) && (
                                        <Box alignSelf="flex-start">
                                          <small className="pt-text-muted">{m.email}</small>
                                        </Box>
                                      )}
                                    </Flex>
                                  </Box>
                                </Flex>
                              </td>
                              <td>
                                {m.customRoles.length ? (
                                  <div>
                                    {m.customRoles.map((r) =>
                                      customRoles && customRoles[r] ? (
                                        <CustomRoleDetails
                                          role={customRoles[r]}
                                          key={r}
                                          member={m}
                                          roleKey={customRoles[r].key}
                                          roles={m.customRoles.map((roleId) => customRoles[roleId]).filter(Boolean)}
                                          roleType="direct"
                                          isMatch={keyMatchesFilter(customRoles[r].key, this.props.roleFilter)}
                                          isDimmed={
                                            roleFilterActive &&
                                            !keyMatchesFilter(customRoles[r].key, this.props.roleFilter)
                                          }
                                        />
                                      ) : null,
                                    )}
                                  </div>
                                ) : (
                                  <Tag intent={baseRoleTagIntent(m, this.props.roleFilter)}>{m.role}</Tag>
                                )}
                                {(() => {
                                  if (settings.enableTeamsVisibility === 'teams_v1') {
                                    return (
                                      <span>
                                        {customRolesWithTeamsForMember(m, customRoles).map(({ role, team }) => (
                                          <CustomRoleDetails
                                            key={role._id}
                                            role={customRoles[role._id]}
                                            member={m}
                                            roleKey={role.key}
                                            // Pass all team roles for carousel functionality
                                            roles={customRolesWithTeamsForMember(m, customRoles)
                                              .map(({ role: r }) => customRoles[r._id])
                                              .filter(Boolean)}
                                            roleType="team"
                                            teamRoleAttributes={this.state.teamData[team.key]?.roleAttributes}
                                            isMatch={keyMatchesFilter(role.key, this.props.roleFilter)}
                                            isDimmed={
                                              roleFilterActive && !keyMatchesFilter(role.key, this.props.roleFilter)
                                            }
                                          />
                                        ))}
                                      </span>
                                    );
                                  }
                                  return <span />;
                                })()}
                              </td>
                              <td>
                                {(() => {
                                  if (settings.enableTeamsVisibility === 'teams_v1') {
                                    if (m.teams) {
                                      return (
                                        <div>
                                          {m.teams.map((team) => (
                                            // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- clickable debug/test affordance, not a real interactive control; making it keyboard-accessible is a UX change out of scope here
                                            <span
                                              key={team.key}
                                              className={`MembersTeams MembersTeams--clickable${
                                                keyMatchesFilter(team.key, this.props.teamFilter)
                                                  ? ' MembersTeams--match'
                                                  : ''
                                              }`}
                                              onClick={() => this.handleTeamClick(team, account)}
                                              title={`Click to test API for team: ${team.name}`}
                                            >
                                              {team.key}
                                            </span>
                                          ))}
                                        </div>
                                      );
                                    }
                                    return <BooleanTag value={false} />;
                                  }
                                  if (settings.enableTeamsVisibility === 'teams_v2') {
                                    if (m.teams) {
                                      const allPairs = customRolesWithTeamsForMember(m, customRoles);
                                      const rolesByTeam = new Map<
                                        string,
                                        Array<{ role: CustomRole; team: TeamsData }>
                                      >();
                                      for (const pair of allPairs) {
                                        if (!rolesByTeam.has(pair.team.key)) {
                                          rolesByTeam.set(pair.team.key, []);
                                        }
                                        rolesByTeam.get(pair.team.key)!.push(pair);
                                      }
                                      for (const team of m.teams) {
                                        if (!rolesByTeam.has(team.key)) {
                                          rolesByTeam.set(team.key, []);
                                        }
                                      }
                                      if (rolesByTeam.size === 0) {
                                        return <BooleanTag value={false} />;
                                      }
                                      return (
                                        <div>
                                          {[...rolesByTeam.entries()].map(([teamKey, pairs]) => (
                                            <div key={teamKey} className="MembersTeamGroup">
                                              <div
                                                className={`MembersTeamGroup__header${
                                                  keyMatchesFilter(teamKey, this.props.teamFilter)
                                                    ? ' MembersTeamGroup__header--match'
                                                    : ''
                                                }`}
                                              >
                                                {teamKey}
                                              </div>
                                              <div className="MembersTeamGroup__roles">
                                                {pairs.length > 0 ? (
                                                  pairs.map(({ role, team }) => (
                                                    <CustomRoleDetails
                                                      key={role._id}
                                                      role={customRoles[role._id]}
                                                      member={m}
                                                      roleKey={role.key}
                                                      roles={allPairs
                                                        .map(({ role: r }) => customRoles[r._id])
                                                        .filter(Boolean)}
                                                      roleType="team"
                                                      teamRoleAttributes={this.state.teamData[team.key]?.roleAttributes}
                                                      onTeamDataNeeded={this.fetchTeamDataIfNeeded}
                                                      teamKey={team.key}
                                                      isMatch={keyMatchesFilter(role.key, this.props.roleFilter)}
                                                      isDimmed={
                                                        roleFilterActive &&
                                                        !keyMatchesFilter(role.key, this.props.roleFilter)
                                                      }
                                                    />
                                                  ))
                                                ) : (
                                                  <span className="MembersTeamGroup__noRoles">No custom roles</span>
                                                )}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    }
                                    return <BooleanTag value={false} />;
                                  }
                                  return <span />;
                                })()}
                              </td>
                              <td style={{ paddingLeft: '20px' }}>
                                {shouldShowConsolidationButton(m, customRoles, customRolesWithTeamsForMember) ? (
                                  <ConsolidationModal
                                    member={m}
                                    account={account}
                                    customRoles={customRoles}
                                    customRolesWithTeamsForMember={customRolesWithTeamsForMember}
                                    teamData={this.state.teamData}
                                    onConsolidate={this.handleConsolidation}
                                    buttonText="view permissions"
                                  />
                                ) : (
                                  <span />
                                )}
                              </td>
                              <td>
                                <MFAStatusTag
                                  scopes={scopes}
                                  status={m.mfa}
                                  role={m.role}
                                  onMFADisable={() => this.props.onSetMfaDisabled(account, m)}
                                  enableMemberMfaReset={settings.enableMemberMfaReset}
                                  mfaEnforced={m.mfaEnforced}
                                  mfaGracePeriodExpiresAt={m.mfaGracePeriodExpiresAt}
                                  externalAuthCategory={(() => {
                                    const cat =
                                      getAuthMethodInfo(m, account.samlConfig, this.props.marketplaceProvider)
                                        ?.category ?? '';
                                    return ['SSO', 'OAuth', 'Marketplace'].includes(cat) ? cat : undefined;
                                  })()}
                                />
                              </td>
                              {settings.enableAuthMethodColumn && (
                                <td>
                                  <AuthMethodTag
                                    member={m}
                                    samlConfig={account.samlConfig}
                                    marketplaceProvider={this.props.marketplaceProvider}
                                  />
                                </td>
                              )}
                              <td>
                                {m._pendingInvite && (
                                  <CircularIconButton
                                    iconName="clock"
                                    intent="warning"
                                    size="medium"
                                    tooltip="Pending invite (click to resend)"
                                    onPress={() => this.props.handleResendInvitation(m._id)}
                                  />
                                )}
                              </td>
                              {settings.enableMemberSessions && (
                                <td>
                                  <TooltipTrigger>
                                    <Focusable>
                                      <span role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
                                        {getMemberLastActive(m._id, 'timeAgo')}
                                      </span>
                                    </Focusable>
                                    <Tooltip placement="top" variant="popover">
                                      {this.getSessionAuthKind(m._id)}
                                    </Tooltip>
                                  </TooltipTrigger>
                                </td>
                              )}

                              <td>
                                <CopyToClipboard content={m._id}>
                                  <code>{m._id}</code>
                                </CopyToClipboard>
                              </td>
                              <td>
                                {m._lastSeen ? (
                                  <TooltipTrigger>
                                    <Focusable>
                                      <span role="button" tabIndex={0} style={{ cursor: 'default' }}>
                                        {formatDistanceToNow(m._lastSeen, { addSuffix: true })}
                                      </span>
                                    </Focusable>
                                    <Tooltip placement="top">{format(m._lastSeen, 'MM/dd/yyyy h:mm a')}</Tooltip>
                                  </TooltipTrigger>
                                ) : (
                                  <span className="pt-text-muted">Never</span>
                                )}
                              </td>
                              <td>
                                <a href={linkToSessionReplay('', m._id)} target="_blank" rel="noopener noreferrer">
                                  <svg fill="currentColor" width="24" height="24" viewBox="0 0 24 24" role="img">
                                    <path d="M8 5v14l11-7z"></path>
                                  </svg>
                                </a>
                              </td>
                              {showActionsColumn && (
                                <td>
                                  <MemberActionsMenu
                                    member={m}
                                    account={account}
                                    samlConfig={account.samlConfig}
                                    scopes={scopes}
                                    onMembersRefresh={() => this.props.onMembersRefresh()}
                                    marketplaceProvider={this.props.marketplaceProvider}
                                  />
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      ) : (
                        <tbody>
                          <tr>
                            <td
                              colSpan={
                                8 +
                                (settings.enableTeamsVisibility !== 'teams_off' ? 1 : 0) +
                                (settings.enableMemberSessions ? 1 : 0) +
                                (settings.enableAuthMethodColumn ? 1 : 0) +
                                (showActionsColumn ? 1 : 0)
                              }
                            >
                              {this.state.searchQuery.trim().length > 0
                                ? `No members found for "${this.state.searchQuery.trim()}"`
                                : 'No members'}
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </>
                  )}
                </>
              </Table>
            </ModuleContent>
          </Module>
        </Box>
      </Flex>
    );
  }

  private getActiveMembersCount = () => {
    const { memberSessions, paginationData } = this.props;
    const { monthsAgo } = this.state;
    const dateNMonthsAgo = subMonths(new Date(), monthsAgo);
    const membersActivePastNMonths = memberSessions.filter(
      (session) => new Date(session.lastActiveDate).getTime() > dateNMonthsAgo.getTime(),
    );
    return `${membersActivePastNMonths.length} / ${paginationData && paginationData.totalCount}`;
  };

  private getSessionAuthKind = (memberId: string) => {
    const memberSession = this.props.getMemberSession(memberId);
    if (memberSession.length) {
      const { authKind } = memberSession[0];
      switch (authKind) {
        case 'session':
          return 'This member was last active on the application';
        case 'token':
        case 'scoped':
          return 'This member was last active using an API access token';
        case 'snippet':
          return 'This member was last active using a snippet';
        case 'oauth2':
          return 'This member was last active using an OAuth application';
        case '':
        default:
          return 'Authorization information is unavailable for this member';
      }
    }
    return 'This member has not been active since we began logging member sessions';
  };

  private handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    this.setState({ searchQuery: query });
    this.debouncedSearch(query);
  };

  private handleRoleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filter = e.target.value;
    this.setState({ roleFilterInput: filter });
    this.debouncedRoleFilter(filter);
  };

  private handleTeamFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filter = e.target.value;
    this.setState({ teamFilterInput: filter });
    this.debouncedTeamFilter(filter);
  };

  // Clearing bypasses the debounce so results update immediately.
  private handleClearSearch = () => {
    this.debouncedSearch.cancel();
    this.setState({ searchQuery: '' });
    this.props.handleSearchMembers('');
  };

  private handleClearRoleFilter = () => {
    this.debouncedRoleFilter.cancel();
    this.setState({ roleFilterInput: '' });
    this.props.handleRoleFilterChange('');
  };

  private handleClearTeamFilter = () => {
    this.debouncedTeamFilter.cancel();
    this.setState({ teamFilterInput: '' });
    this.props.handleTeamFilterChange('');
  };

  // Renders a clear (X) button for a filter input, only when it has a value.
  private renderClearButton(value: string, onClear: () => void) {
    if (!value) {
      return undefined;
    }
    return <Button minimal small icon="cross" aria-label="Clear filter" onClick={onClear} />;
  }

  private updateTeamData = (teamKey: string, roleAttributes: { [attributeName: string]: string[] }) => {
    this.setState((prevState) => ({
      teamData: {
        ...prevState.teamData,
        [teamKey]: { roleAttributes },
      },
    }));
  };

  private fetchTeamDataIfNeeded = async (teamKey: string) => {
    const { account } = this.props;
    if (this.state.teamData[teamKey]?.roleAttributes) {
      return;
    }

    try {
      const response = await observableToPromise(getTeamWithRolesAndAttributes(teamKey, account));
      this.updateTeamData(teamKey, response.roleAttributes || {});
    } catch (error) {
      console.error(`Failed to fetch team data for ${teamKey}:`, error);
    }
  };

  private fetchAllMemberTeamData = async (member: Member) => {
    const { account, customRoles, customRolesWithTeamsForMember } = this.props;

    const memberTeams = customRolesWithTeamsForMember(member, customRoles);
    const uniqueTeamKeys = [...new Set(memberTeams.map(({ team }) => team.key))];

    const teamsNeedingData = uniqueTeamKeys.filter((teamKey) => !this.state.teamData[teamKey]?.roleAttributes);

    if (teamsNeedingData.length === 0) {
      return this.state.teamData;
    }

    const fetchPromises = teamsNeedingData.map(async (teamKey) => {
      try {
        const response = await observableToPromise(getTeamWithRolesAndAttributes(teamKey, account));
        return { teamKey, roleAttributes: response.roleAttributes || {} };
      } catch (error) {
        console.error(`Failed to fetch team data for ${teamKey}:`, error);
        return { teamKey, roleAttributes: {} };
      }
    });

    const fetchResults = await Promise.all(fetchPromises);

    const updatedTeamData = { ...this.state.teamData };
    fetchResults.forEach(({ teamKey, roleAttributes }) => {
      updatedTeamData[teamKey] = {
        ...updatedTeamData[teamKey],
        roleAttributes,
      };
      this.updateTeamData(teamKey, roleAttributes);
    });

    return updatedTeamData;
  };

  private handleTeamClick = (team: TeamsData, account: Account) => {
    observableToPromise(getTeamWithRolesAndAttributes(team.key, account))
      .then((response: Team) => {
        this.setState((prevState) => ({
          teamData: {
            ...prevState.teamData,
            [team.key]: {
              roleAttributes: response.roleAttributes || {},
            },
          },
        }));
      })
      .catch((e) => {
        console.error('Failed to fetch team data:', e);
      });
  };

  private handleConsolidation = async (member: Member, account: Account) => {
    const updatedTeamData = await this.fetchAllMemberTeamData(member);

    const dependencies = {
      customRoles: this.props.customRoles,
      customRolesWithTeamsForMember: this.props.customRolesWithTeamsForMember,
      teamData: updatedTeamData,
      updateTeamData: this.updateTeamData,
    };

    return consolidatePermissions(member, account, dependencies);
  };
}
